import React, { useEffect, useMemo, useState } from 'react';
import { Background, BackgroundVariant, BaseEdge, EdgeLabelRenderer, Handle, Position, ReactFlow, getBezierPath } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import {
  CircleCheck, CircleDot, Clock3, ExternalLink, Hourglass, Inbox, MinusCircle, OctagonX, PenLine, Play, Send, ShieldCheck, Sparkles, SpellCheck, UserCheck, UserRound,
} from 'lucide-react';
import axiosInstance from '../utils/axiosInstance';
import V2Shell from './V2Shell';

// Live flow: a diagram of how the last 24 hours of tickets moved through Replai (Geoff, 2026-11-03).
const API = 'api/replai/v2/flow/';
const GORGIAS = 'https://lamptwist.gorgias.com/app/ticket/';
const clock = (iso) => (iso ? new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '');
const TONE = {
  done: 'bg-[var(--line-2)] text-[var(--ink-2)]',
  good: 'bg-emerald-100 text-emerald-800',
  warn: 'bg-amber-100 text-amber-800',
  now: 'bg-blue-100 text-blue-800',
};

// The steps of one ticket, in order.
const steps = (r) => {
  const out = [{ label: `In ${clock(r.at)}`, tone: 'done' }];
  if (r.audience !== 'customer') {
    out.push({ label: r.would_close ? 'Thank-you, nothing to do' : 'For a person', tone: r.would_close ? 'done' : 'warn' });
    return out;
  }
  out.push({ label: 'Drafted', tone: 'done' });
  if (r.score != null) out.push({ label: `Facts ${r.score}/5`, tone: r.score >= 4 ? 'done' : 'warn' });
  if (r.corrected) out.push({ label: 'Facts corrected', tone: 'good' });
  if (r.polished) out.push({ label: 'Wording improved', tone: 'good' });
  if (r.review_ok === false) out.push({ label: 'Review: a person looks', tone: 'warn' });
  if (r.status === 'sent') out.push({ label: `Sent ${clock(r.sent_at)}`, tone: 'good' });
  else if (r.approve_first && r.status === 'queued') out.push({ label: 'Waits for Approve', tone: 'warn' });
  else if (r.status === 'queued' || r.status === 'approved') out.push({ label: `Goes out ${clock(r.send_at)}`, tone: 'now' });
  else if (r.status) out.push({ label: r.status === 'blocked' ? 'Blocked' : 'Held', tone: 'warn' });
  else out.push({ label: r.band === 'high' ? 'Ready for an agent' : 'Check before sending', tone: 'warn' });
  return out;
};

// The diagram: every step of a ticket, drawn with React Flow.
const isWaiting = (r) => r.status === 'queued' || r.status === 'approved';
const isHeld = (r) => ['held', 'blocked', 'failed'].includes(r.status);
const forCustomer = (r) => r.audience === 'customer';
const NODE_W = 172;
// How a ticket got its answer: the end of the line, and the automation rate above the diagram.
const usedDraft = (r) => ['sent', 'copied', 'edited'].includes(r.how);
const HOW = {
  auto: { title: 'Lia by herself', short: 'by Lia', of: (r) => r.how === 'auto' },
  approved: { title: 'approved by a person', short: 'Approved by', of: (r) => r.how === 'approved' },
  sent: { title: 'Send button', short: 'Sent by', of: (r) => r.how === 'sent' },
  copied: { title: 'copied as it is', short: 'Copied by', of: (r) => r.how === 'copied' },
  edited: { title: 'copied and edited', short: 'Edited by', of: (r) => r.how === 'edited' },
  own: { title: 'their own reply', short: 'Own reply by', of: (r) => r.how === 'own' },
};
const STEPS = {
  in: { x: 0, y: 92, title: 'Came in', icon: Inbox, of: () => true },
  draft: { x: 214, y: 92, title: 'Replai drafts', icon: Sparkles, of: () => true },
  person: { x: 428, y: -72, title: 'Note for a person', icon: UserRound, tone: 'warn', of: (r) => !forCustomer(r) && !r.would_close },
  facts: { x: 428, y: 92, title: 'Fact check', icon: ShieldCheck, of: forCustomer },
  nothing: { x: 428, y: 184, title: 'Thanks, closed', icon: MinusCircle, of: (r) => !forCustomer(r) && r.would_close },
  read: { x: 642, y: 92, title: 'Last read', icon: SpellCheck, of: forCustomer },
  agent: { x: 856, y: 0, title: 'Agent checks draft', icon: UserCheck, tone: 'warn', of: (r) => forCustomer(r) && !r.status },
  approve: { x: 856, y: 92, title: 'Waits for Approve', icon: Hourglass, tone: 'warn', of: (r) => r.status === 'queued' && !!r.approve_first },
  queue: { x: 856, y: 184, title: 'Goes out soon', icon: Clock3, tone: 'now', of: (r) => isWaiting(r) && !(r.status === 'queued' && r.approve_first) },
  own: { x: 1070, y: -144, title: 'Own reply', icon: PenLine, of: (r) => r.how === 'own' },
  team: { x: 1070, y: -72, title: 'Agent used draft', icon: Send, tone: 'good', of: usedDraft },
  sent: { x: 1070, y: 92, title: 'Sent by Lia', icon: Send, tone: 'good', of: (r) => r.status === 'sent' },
  held: { x: 1070, y: 184, title: 'Held or blocked', icon: OctagonX, tone: 'warn', of: isHeld },
};
const LINKS = [
  ['in', 'draft', () => true],
  ['draft', 'person', STEPS.person.of],
  ['draft', 'facts', forCustomer],
  ['draft', 'nothing', STEPS.nothing.of],
  ['facts', 'read', forCustomer],
  ['read', 'agent', STEPS.agent.of],
  ['read', 'approve', (r) => !!r.approve_first && forCustomer(r)],
  ['read', 'queue', (r) => forCustomer(r) && !!r.status && !r.approve_first],
  ['approve', 'sent', (r) => !!r.approve_first && r.status === 'sent'],
  ['queue', 'sent', (r) => !r.approve_first && r.status === 'sent'],
  ['queue', 'held', (r) => isHeld(r)],
  ['person', 'team', (r) => !forCustomer(r) && usedDraft(r)],
  ['person', 'own', (r) => !forCustomer(r) && r.how === 'own'],
  ['agent', 'team', (r) => STEPS.agent.of(r) && usedDraft(r)],
  ['agent', 'own', (r) => STEPS.agent.of(r) && r.how === 'own'],
];
const TONES = {
  plain: { icon: 'var(--ink-2)', tint: 'var(--line-2)' },
  warn: { icon: '#b45309', tint: '#fef3c7' },
  now: { icon: '#1d4ed8', tint: '#dbeafe' },
  good: { icon: '#047857', tint: '#d1fae5' },
};
const RECENT_MS = 30 * 60 * 1000;
// The final status of the ticket in Gorgias, whatever step it reached.
const FINALS = {
  closed: { title: 'Closed', icon: CircleCheck, of: (r) => r.closed },
  open: { title: 'Still open', icon: CircleDot, of: (r) => !r.closed },
  waiting: { title: 'Still waiting on us', icon: Hourglass, of: (r) => isWaitingOnUs(r) },
};
const groupOf = (key) => (key.startsWith('how:') ? HOW[key.slice(4)] : STEPS[key] || FINALS[key]);
// Still waiting for someone: nobody answered yet and the ticket is open.
const isWaitingOnUs = (r) => !r.closed && !r.answered && !r.sent_by && !r.how && r.status !== 'sent';
// What a block means for what is in it: passed through, still in it, or finished.
const blockNote = (id, rows) => {
  if (['in', 'draft', 'facts', 'read'].includes(id)) return { text: 'passed through', tone: 'plain' };
  if (id === 'sent') return { text: rows.length ? `final, ${rows.filter(HOW.approved.of).length} approved` : '', tone: 'done' };
  if (id === 'nothing' || id === 'team' || id === 'own') return { text: rows.length ? 'final' : '', tone: 'done' };
  if (id === 'queue' || id === 'approve') return { text: rows.length ? `${rows.length} still here` : 'none waiting', tone: rows.length ? 'wait' : 'plain' };
  const still = rows.filter(isWaitingOnUs).length;
  return { text: still ? `${still} still waiting` : 'none waiting', tone: still ? 'wait' : 'plain' };
};
const NOTE_TEXT = { plain: 'text-[var(--ink-3)]', done: 'text-[#047857]', wait: 'font-semibold text-[#b45309]' };

const StepNode = ({ data }) => {
  const Icon = data.icon;
  const tone = TONES[data.tone || 'plain'];
  return (
    <div style={{ width: NODE_W }}
      className={`flex cursor-pointer items-center gap-2.5 rounded-xl border bg-[var(--surface)] px-2.5 py-2 shadow-sm transition-shadow hover:shadow-md ${data.active ? 'border-[var(--ink)] ring-1 ring-[var(--ink)]' : 'border-[var(--line)]'} ${data.n ? '' : 'opacity-50'}`}>
      <Handle type="target" position={Position.Left} className="!h-px !w-px !min-h-0 !min-w-0 !border-0 !opacity-0" isConnectable={false} />
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg" style={{ background: tone.tint, color: tone.icon }}>
        <Icon size={16} strokeWidth={2} aria-hidden="true" />
      </span>
      <span className="min-w-0">
        <span className="block text-[1.05rem] font-semibold leading-none text-[var(--ink)]">{data.n}</span>
        <span className="mt-1 block truncate text-[0.7rem] leading-none text-[var(--ink-2)]">{data.title}</span>
        {data.note?.text && <span className={`mt-1 block truncate text-[0.62rem] leading-none ${NOTE_TEXT[data.note.tone]}`}>{data.note.text}</span>}
      </span>
      <Handle type="source" position={Position.Right} className="!h-px !w-px !min-h-0 !min-w-0 !border-0 !opacity-0" isConnectable={false} />
    </div>
  );
};

const FlowEdge = ({ id, sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition, data }) => {
  const [path, labelX, labelY] = getBezierPath({ sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition });
  return (
    <>
      <BaseEdge id={id} path={path} style={{ stroke: data.n ? 'var(--ink-3)' : 'var(--line)', strokeWidth: data.n ? 1.25 + 4 * data.share : 1, strokeOpacity: data.n ? 0.55 : 1, strokeDasharray: data.n ? undefined : '3 4' }} />
      {data.moving > 0 && Array.from({ length: Math.min(data.moving, 3) }, (_, i) => (
        <circle key={i} r="3" fill="#2563eb">
          <animateMotion dur="3s" begin={`-${i}s`} repeatCount="indefinite" path={path} />
        </circle>))}
      {data.n > 0 && (
        <EdgeLabelRenderer>
          <span className="nodrag nopan pointer-events-none absolute rounded-full bg-[var(--paper)] px-1 text-[0.65rem] font-medium text-[var(--ink-2)]"
            style={{ transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)` }}>{data.n}</span>
        </EdgeLabelRenderer>)}
    </>
  );
};
const NODE_TYPES = { step: StepNode };
const EDGE_TYPES = { flow: FlowEdge };

export const FlowDiagram = ({ rows, selected, onSelect, now = Date.now() }) => {
  const still = typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  const recent = useMemo(() => rows.filter((r) => now - new Date(r.at).getTime() < RECENT_MS), [rows, now]);
  const nodes = useMemo(() => Object.entries(STEPS).map(([id, step]) => ({
    id, type: 'step', position: { x: step.x, y: step.y }, draggable: false, connectable: false,
    data: { ...step, n: rows.filter(step.of).length, active: selected === id,
      note: blockNote(id, rows.filter(step.of)) },
    ariaLabel: `${step.title}: ${rows.filter(step.of).length} tickets`,
  })), [rows, selected]);
  const edges = useMemo(() => LINKS.map(([a, b, on]) => {
    const n = rows.filter(on).length;
    return { id: `${a}-${b}`, source: a, target: b, type: 'flow',
      data: { n, share: n / Math.max(1, rows.length), moving: still ? 0 : recent.filter(on).length } };
  }), [rows, recent, still]);
  return (
    <div className="h-[330px] overflow-hidden rounded-xl border border-[var(--line)] bg-[var(--paper)]">
      <ReactFlow nodes={nodes} edges={edges} nodeTypes={NODE_TYPES} edgeTypes={EDGE_TYPES} fitView fitViewOptions={{ padding: 0.06 }}
        onNodeClick={(_, node) => onSelect(selected === node.id ? '' : node.id)}
        nodesDraggable={false} nodesConnectable={false} elementsSelectable={false} panOnDrag={false} zoomOnScroll={false}
        zoomOnPinch={false} zoomOnDoubleClick={false} preventScrolling={false} minZoom={0.3} proOptions={{ hideAttribution: true }}>
        <Background variant={BackgroundVariant.Dots} gap={18} size={1} color="var(--line)" />
      </ReactFlow>
    </div>
  );
};

const howLabel = (r) => {
  if (!r.how) return '';
  if (r.how === 'auto') return `Sent ${clock(r.sent_at)} by Lia`;
  return `${HOW[r.how].short} ${r.how_by || 'someone'}`;
};

// The furthest step a ticket reached, for its row.
const lastStep = (r) => (steps(r).at(-1));
const allSteps = (r) => (r.how && r.how !== 'auto' ? [...steps(r), { label: howLabel(r), tone: r.how === 'own' ? 'done' : 'good' }] : steps(r));

const canSend = (r) => forCustomer(r) && !r.closed && !r.sent_by && !r.how && !['sent', 'held', 'blocked', 'failed'].includes(r.status);

const Row = ({ r }) => {
  const [open, setOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [note, setNote] = useState('');
  const [gone, setGone] = useState(false);
  const last = lastStep(r);
  const send = async () => {
    setSending(true);
    setNote('');
    try {
      if (r.sr) {
        const { data } = await axiosInstance.patch(`api/replai/v2/scheduled/${r.sr}/`, { action: 'approve' });
        if (data.status === 'sent') setGone(true);
        else setNote(data.hold_reason || 'Not sent.');
      } else {
        await axiosInstance.post(`api/replai/v2/send/${r.run}/`, { reviewed: true });
        setGone(true);
      }
    } catch (e) {
      setNote(e.response?.data?.error || 'Not sent. Try again in a moment.');
    }
    setSending(false);
  };
  return (
    <li>
      <div className="flex items-center hover:bg-[var(--line-2)]">
        <button type="button" onClick={() => setOpen(!open)} aria-expanded={open}
          className="flex min-w-0 flex-1 items-center gap-3 px-3 py-1.5 text-left text-xs">
          <span className="w-10 shrink-0 text-[var(--ink-3)]">{clock(r.at)}</span>
          <span className="w-36 shrink-0 truncate font-medium text-[var(--ink)]">{r.customer || `Ticket ${r.ticket}`}</span>
          <span className={`shrink-0 rounded-full px-2 py-0.5 font-medium ${TONE[gone || (r.how && r.how !== 'own') ? 'good' : r.how === 'own' ? 'done' : last.tone]}`}>{gone ? 'Sent just now' : howLabel(r) || last.label}</span>
          {r.score != null && <span className="shrink-0 text-[var(--ink-3)]">Facts {r.score}/5</span>}
          {r.confidence != null && <span className="shrink-0 text-[var(--ink-3)]">{Math.round(r.confidence * 100)}%</span>}
          <span className="min-w-0 flex-1 truncate text-[var(--ink-2)]">{r.why || r.subject}</span>
          <span className={`shrink-0 font-medium ${isWaitingOnUs(r) ? 'text-[#b45309]' : 'text-[var(--ink-3)]'}`}>{r.closed ? 'Closed' : !isWaitingOnUs(r) || gone ? 'Answered' : 'Waiting'}</span>
        </button>
        <span className="w-9 shrink-0 pr-2 text-right">
          {canSend(r) && !gone && (
            <button type="button" onClick={() => setOpen(true)} aria-label={`Read and send the reply to ${r.customer || 'the customer'}`} title="Read and send"
              className="inline-flex h-6 w-6 items-center justify-center rounded-full text-[#1d4ed8] hover:bg-[#dbeafe]">
              <Play size={13} fill="currentColor" aria-hidden="true" />
            </button>)}
        </span>
      </div>
      {open && (
        <div className="space-y-2 border-t border-[var(--line-2)] bg-[var(--paper)] px-3 py-2 text-xs">
          <div className="flex flex-wrap items-center gap-1">
            {allSteps(r).map((s) => <span key={s.label} className={`rounded-full px-2 py-0.5 font-medium ${TONE[s.tone]}`}>{s.label}</span>)}
            <a href={`${GORGIAS}${r.ticket}`} target="_blank" rel="noreferrer" className="ml-2 inline-flex items-center gap-1 underline">
              Open in Gorgias <ExternalLink size={12} aria-hidden="true" />
            </a>
          </div>
          <p className="text-[var(--ink-2)]">{r.subject}{r.category ? `, ${r.category.replaceAll('_', ' ')}` : ''}</p>
          {r.why && <p className="text-[var(--ink)]"><b>Why not automatic:</b> {r.why}{r.confidence != null ? ` Confidence ${Math.round(r.confidence * 100)}%${r.threshold != null && r.audience === 'customer' ? `, bar ${Math.round(r.threshold * 100)}%.` : '.'}` : ''}</p>}
          {r.draft && !r.polished && <pre className="max-h-64 overflow-y-auto whitespace-pre-wrap rounded border border-[var(--line)] bg-white p-2 font-sans">{r.draft}</pre>}
          {r.polished && (
            <div className="grid gap-2 md:grid-cols-2">
              <div><b>Before</b><pre className="mt-1 whitespace-pre-wrap rounded border border-[var(--line)] bg-white p-2 font-sans">{r.polished.was}</pre></div>
              <div><b>After:</b> {r.polished.because}<pre className="mt-1 whitespace-pre-wrap rounded border border-[var(--line)] bg-white p-2 font-sans">{r.draft}</pre></div>
            </div>)}
          {canSend(r) && !gone && (
            <button type="button" onClick={send} disabled={sending}
              className="inline-flex items-center gap-1.5 rounded-md bg-[var(--ink)] px-3 py-1.5 font-semibold text-white disabled:opacity-50">
              <Play size={12} fill="currentColor" aria-hidden="true" /> {sending ? 'Sending…' : 'Send to customer'}
            </button>)}
          {gone && <p className="font-semibold text-emerald-800">Sent to the customer.</p>}
          {note && <p className="font-semibold text-red-700">{note}</p>}
        </div>)}
    </li>
  );
};

// How the answered tickets got their answer: fully automatic, semi-automatic (a person used our reply), or by hand.
const RATES = [
  { label: 'Automated', keys: ['auto'] },
  { label: 'Semi-automated', keys: ['approved', 'sent', 'copied', 'edited'] },
  { label: 'By hand', keys: ['own'] },
];
export const Rates = ({ rows, selected, onSelect }) => {
  const answered = rows.filter((r) => r.how).length;
  return (
    <section className="mt-5 rounded-xl border border-[var(--line)] bg-[var(--surface)] p-3" aria-label="How tickets were answered">
      <h2 className="text-xs font-semibold text-[var(--ink)]">How the {answered} answered tickets were answered</h2>
      <div className="mt-2 grid gap-3 md:grid-cols-3">
        {RATES.map(({ label, keys }) => {
          const n = rows.filter((r) => keys.includes(r.how)).length;
          return (
            <div key={label} className="min-w-0">
              <p className="text-[var(--ink)]"><span className="text-[1.4rem] font-semibold leading-none">{answered ? Math.round((100 * n) / answered) : 0}%</span> <span className="text-xs text-[var(--ink-2)]">{label}, {n}</span></p>
              <div className="mt-1 flex flex-wrap gap-1">
                {keys.map((k) => (
                  <button key={k} type="button" aria-pressed={selected === `how:${k}`} onClick={() => onSelect(selected === `how:${k}` ? '' : `how:${k}`)}
                    className={`rounded-full border px-2 py-0.5 text-xs ${selected === `how:${k}` ? 'border-[var(--ink)] bg-[var(--ink)] text-white' : 'border-[var(--line)] text-[var(--ink-2)] hover:bg-[var(--line-2)]'}`}>
                    {rows.filter(HOW[k].of).length} {HOW[k].title}
                  </button>))}
              </div>
            </div>);
        })}
      </div>
    </section>
  );
};

// Why tickets did not go out by themselves, one bar per reason.
const Breakdown = ({ title, rows, group, onGroup }) => {
  const groups = useMemo(() => {
    const c = {};
    rows.forEach((r) => { if (r.group) c[r.group] = (c[r.group] || 0) + 1; });
    return Object.entries(c).sort((a, b) => b[1] - a[1]);
  }, [rows]);
  const max = groups.length ? groups[0][1] : 1;
  return (
    <section className="min-w-0 rounded-xl border border-[var(--line)] bg-[var(--surface)] p-3">
      <h2 className="text-xs font-semibold text-[var(--ink)]">{title}: {rows.length}, <span className="text-[#b45309]">{rows.filter(isWaitingOnUs).length} still waiting</span></h2>
      <ul className="mt-2 space-y-0.5">
        {groups.length === 0 && <li className="text-xs text-[var(--ink-3)]">Nothing here.</li>}
        {groups.map(([name, n]) => (
          <li key={name}>
            <button type="button" aria-pressed={group === name} onClick={() => onGroup(group === name ? '' : name)}
              className={`grid w-full grid-cols-[minmax(0,11rem)_1fr_2rem] items-center gap-2 rounded px-1 py-0.5 text-left text-xs hover:bg-[var(--line-2)] ${group === name ? 'bg-[var(--line-2)] font-semibold' : ''}`}>
              <span className="truncate text-[var(--ink-2)]" title={name}>{name}</span>
              <span className="h-1.5 rounded-full bg-[var(--line-2)]"><span className="block h-1.5 rounded-full bg-[#b45309]" style={{ width: `${(100 * n) / max}%` }} /></span>
              <span className="text-right font-medium tabular-nums text-[var(--ink)]">{n}</span>
            </button>
          </li>))}
      </ul>
    </section>
  );
};

const FlowV2 = () => {
  const [d, setD] = useState(null);
  const [selected, setSelected] = useState('');
  const [group, setGroup] = useState('');
  useEffect(() => {
    let alive = true;
    const load = () => axiosInstance.get(API).then(({ data }) => alive && setD(data)).catch(() => alive && setD(false));
    load();
    const t = setInterval(load, 20000);
    return () => { alive = false; clearInterval(t); };
  }, []);
  const c = (d && d.counts) || {};
  const rows = d ? (group ? d.rows.filter((r) => r.group === group) : selected ? d.rows.filter(groupOf(selected).of) : d.rows) : [];
  return (
    <V2Shell>
      <div className="px-6 py-10 sm:px-10 xl:px-14">
        <h1 className="text-[2.4rem] font-semibold leading-[1.1]">Live flow</h1>
        <p className="prose-body mt-2 max-w-3xl text-[var(--ink-2)]">
          The last 24 hours of tickets. Click a step to see its tickets and why they landed there.
        </p>
        {d === null && <p className="mt-6 text-[var(--ink-3)]">Loading…</p>}
        {d === false && <p className="mt-6 text-red-700">Could not load the flow.</p>}
        {d && (
          <>
            <Rates rows={d.rows} selected={selected} onSelect={(k) => { setSelected(k); setGroup(''); }} />
            <div className="mt-3"><FlowDiagram rows={d.rows} selected={selected} onSelect={(k) => { setSelected(k); setGroup(''); }} /></div>
          <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
            <span className="text-[var(--ink-2)]">Where tickets stand now:</span>
            {Object.entries(FINALS).map(([key, f]) => {
              const Icon = f.icon;
              return (
                <button key={key} type="button" aria-pressed={selected === key} onClick={() => { setSelected(selected === key ? '' : key); setGroup(''); }}
                  className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 font-medium ${selected === key ? 'border-[var(--ink)] bg-[var(--ink)] text-white' : 'border-[var(--line)] bg-[var(--surface)] text-[var(--ink)] hover:bg-[var(--line-2)]'}`}>
                  <Icon size={13} aria-hidden="true" /> {d.rows.filter(f.of).length} {f.title.toLowerCase()}
                </button>);
            })}
          </div>
            <div className="mt-3 grid gap-3 md:grid-cols-2">
            <Breakdown title="Why a note for a person" rows={d.rows.filter(STEPS.person.of)} group={group} onGroup={(g) => { setGroup(g); setSelected(''); }} />
            <Breakdown title="Why an agent checks the draft" rows={d.rows.filter(STEPS.agent.of)} group={group} onGroup={(g) => { setGroup(g); setSelected(''); }} />
          </div>
          <h2 className="mt-4 text-xs font-semibold text-[var(--ink-2)]">
              {group ? `${group} (${rows.length})` : selected ? `${groupOf(selected).title} (${rows.length})` : `All tickets (${rows.length})`}
              {(selected || group) && <button type="button" onClick={() => { setSelected(''); setGroup(''); }} className="ml-3 font-normal underline">Show all</button>}
            </h2>
            <ul className="mt-2 max-h-[46vh] divide-y divide-[var(--line-2)] overflow-y-auto rounded-lg border border-[var(--line)] bg-[var(--surface)]">
              {rows.length === 0 && <li className="px-3 py-2 text-xs text-[var(--ink-3)]">No tickets here.</li>}
              {rows.map((r) => <Row key={r.run} r={r} />)}
            </ul>
          </>)}
      </div>
    </V2Shell>
  );
};

export default FlowV2;
